import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {IUserRoleRecord} from "../../models/Settings";
import {
    CommandBar,
    IColumn,
    ICommandBarItemProps,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    SelectionMode,
    ShimmeredDetailsList
} from "@fluentui/react";
import UserDetailsDialog from "../../components/UserManagement/UserDetailsDialog";
import useCan, {USER_MODIFY} from "../../permissions/ability"
import {
    useLazyGetOrganizationEntitlementsQuery,
    useLazyGetOrganizationQuery
} from "../../store/endpoints/OrganizationsEndpoints";

const UserManagementPage: React.FC = () => {
    const auth = useSelector((state: RootState) => state.auth);
    const selectedOrg = useSelector((state: RootState) => state.settings.selectedOrganization);
    const [users, setUsers] = useState<IUserRoleRecord[] | undefined>(undefined);
    const [userLimit, setUserLimit] = useState<number | undefined>(undefined);
    const [licensedUserCount, setLicensedUserCount] = useState<number | undefined>(undefined);
    const [error, setError] = useState<string | undefined>(undefined)
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedUser, setSelectedUser] = useState<IUserRoleRecord | undefined>(undefined)
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [lazyOrg] = useLazyGetOrganizationQuery();
    const [lazyOrgEntitlements] = useLazyGetOrganizationEntitlementsQuery();
    
    const refreshEntitlements = () => {
        if (!selectedOrg)
            return;
        lazyOrgEntitlements(selectedOrg)
            .unwrap()
            .then(a => {
                setUserLimit(a.userLimit);
                setLicensedUserCount(a.licensedUserCount);
            })
            .catch(e => {
                setError('Failed to load entitlements: ' + (e?.message || 'Unknown error'));
            });
    };

    useEffect(() => {
        if (!loading && users === undefined && auth.isAuthed && error === undefined && selectedOrg && selectedOrg !== '') {
            lazyOrg(selectedOrg)
                .unwrap()
                .then(a => {
                    setUsers(a.users);
                })
                .catch(e => {
                    setError('Failed to load users: ' + (e?.message || 'Unknown error'));
                });
            refreshEntitlements();
        }
    }, [loading, users, auth, error]);
    

    const columns: IColumn[] = [
        {
            name: 'Username',
            key: 'username',
            fieldName: 'username',
            minWidth: 100,
        },
        {
            name: 'Role',
            key: 'role',
            fieldName: 'role',
            minWidth: 200,
            maxWidth: 300
        },
        {
            name: 'Actions',
            key: 'actions',
            minWidth: 100,
            maxWidth: 100,
            onRender: (user: IUserRoleRecord) => <PrimaryButton
                text='Details'
                onClick={() => {
                    setSelectedUser(user);
                    setDialogOpen(true);
                }}
            />
        }
    ]

    const commands: ICommandBarItemProps[] = [
        {
            key: 'new',
            text: 'New',
            iconProps: {iconName: 'Add'},
            onClick: () => setDialogOpen(true)
        }
    ]

    const canModify = useCan(USER_MODIFY);

    return (
        <div className='main-content'>
            <h1>User Management</h1>
            {error ?
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                >
                    {error}
                </MessageBar>
                : null
            }
            {canModify ? (
                <CommandBar items={commands}/>
            ) : null}
            {(licensedUserCount && userLimit) ?
                <MessageBar
                    messageBarType={licensedUserCount < userLimit ? MessageBarType.info : MessageBarType.warning}
                    isMultiline={false}>
                    You are using {licensedUserCount} of {userLimit} licenses.
                </MessageBar>
                : null
            }
            <ShimmeredDetailsList selectionMode={SelectionMode.none} enableShimmer={loading} items={users ?? []} columns={columns}/>

            <UserDetailsDialog
                isOpen={dialogOpen}
                onUpdate={(user) => {
                    setUsers(prev => {
                        if (prev === undefined)
                            return prev;
                        if (prev.some(u => u.username === user.username))
                            return prev.map(u => u.username === user.username ? {...u, ...user} : u);
                        return [...prev, user];
                    });
                    refreshEntitlements();
                    setSelectedUser(undefined);
                    setDialogOpen(false);
                }}
                onClose={() => {
                    setSelectedUser(undefined);
                    setDialogOpen(false);
                }}
                onDelete={(username) => {
                    setUsers(prev => prev?.filter(u => u.username !== username));
                    refreshEntitlements();
                    setSelectedUser(undefined);
                    setDialogOpen(false);
                }}
                user={selectedUser}
            />
        </div>
    )
}

export default UserManagementPage;